/**
 * @file "Needs me" on Home: rows of a large number then its label, "forms past
 * due", "line items past promised date" and "invoices overdue 60+ days".
 * Consumer: `HomePageContent`, which passes the My tasks read and the `self`
 * goal progress, so Needs me opens no listener. `self` is null until analytics
 * is configured, and then only forms past due renders. Forms past due counts
 * the listed forms, shows loading until `parameters/analytics` and the forms
 * have loaded, and only then reads "Not configured" while no due-date field is
 * bound;
 * the other two read the My sales summary, the past-promised count linking to
 * the filtered Orders tab. An unanswered figure renders its refusal, never 0.
 */
import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Spin, Tooltip, Typography, theme } from 'antd';

import {
  analyticsViewSearch,
  type AnalyticsCountFigure,
  type AnalyticsSummaryProgress,
} from '@/analytics';
import { AnalyticsCardTitle } from '@/components/analytics/AnalyticsCardTitle';
import { AnalyticsUnansweredFigure } from '@/components/analytics/AnalyticsUnansweredFigure';

import FlexBox from '../../../atoms/FlexBox';
import FlexCard from '../../../atoms/FlexCard';
import { HOME_MY_TASKS_LIMIT } from '../../HOME_MY_TASKS_LIMIT';
import type { HomeMyTasksRead } from '../../HomeMyTasks.types';
import { homeOverdueForms } from '../../homeOverdueForms';
import { HomeSummaryProgress } from '../HomeSummaryProgress';

const { Text } = Typography;

const PAST_PROMISED_SEARCH = analyticsViewSearch({
  tab: 'orders',
  scope: 'self',
  period: 'ytd',
  status: null,
  pastPromised: true,
  project: null,
});

const FormsPastDue: FC<{ myTasks: HomeMyTasksRead }> = ({ myTasks }) => {
  if (myTasks.status === 'loading')
    return <Spin size="small" aria-label="Loading forms past due" />;
  if (myTasks.dueField === null)
    return (
      <Tooltip title="No form due date field is set. Choose one in Settings → Analytics.">
        <Text type="secondary">Not configured</Text>
      </Tooltip>
    );
  switch (myTasks.status) {
    case 'noRight':
      return (
        <Tooltip title="My tasks needs the formsRead or formsWrite right.">
          <Text disabled>—</Text>
        </Tooltip>
      );
    case 'failed':
      return <Text type="danger">{myTasks.message}</Text>;
    case 'found':
      return (
        <Tooltip
          title={`Counts only your ${HOME_MY_TASKS_LIMIT} most recent assigned forms.`}
        >
          <span>{homeOverdueForms(myTasks.tasks)}</span>
        </Tooltip>
      );
  }
};

const NOT_VISIBLE = 'Needs a metrics read right.';

const CountFigure: FC<{
  figure: AnalyticsCountFigure;
  children: (count: number) => ReactNode;
}> = ({ figure, children }) =>
  figure.status === 'answered' ? (
    children(figure.count)
  ) : (
    <AnalyticsUnansweredFigure figure={figure} />
  );

const NeedsMeRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => {
  const { token } = theme.useToken();
  return (
    <FlexBox
      noGrow
      justifyStart
      role="group"
      aria-label={label}
      gap={10}
      style={{
        alignItems: 'baseline',
        paddingBottom: token.paddingXS,
        borderBottom: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
      }}
    >
      <Text
        strong
        style={{ fontSize: token.fontSizeXL, minWidth: token.controlHeight }}
      >
        {children}
      </Text>
      <Text>{label}</Text>
    </FlexBox>
  );
};

const PAST_PROMISED = 'line items past promised date';

const OVERDUE_INVOICES = 'invoices overdue 60+ days';

const SummaryRows: FC<{ self: AnalyticsSummaryProgress }> = ({ self }) => (
  <>
    <NeedsMeRow label={PAST_PROMISED}>
      <HomeSummaryProgress
        progress={self}
        label={PAST_PROMISED}
        notVisibleTooltip={NOT_VISIBLE}
      >
        {({ pastPromised }) => (
          <CountFigure figure={pastPromised}>
            {(count) => (
              <Link
                to={{ pathname: '/analytics', search: PAST_PROMISED_SEARCH }}
                aria-label="Open past-promised lines in Orders"
              >
                {count}
              </Link>
            )}
          </CountFigure>
        )}
      </HomeSummaryProgress>
    </NeedsMeRow>
    <NeedsMeRow label={OVERDUE_INVOICES}>
      <HomeSummaryProgress
        progress={self}
        label={OVERDUE_INVOICES}
        notVisibleTooltip={NOT_VISIBLE}
      >
        {({ overdueInvoices }) => (
          <CountFigure figure={overdueInvoices}>{(count) => count}</CountFigure>
        )}
      </HomeSummaryProgress>
    </NeedsMeRow>
  </>
);

export const HomeNeedsMe: FC<{
  myTasks: HomeMyTasksRead;
  self: AnalyticsSummaryProgress | null;
}> = ({ myTasks, self }) => (
  <FlexCard role="region" aria-label="Needs me" gap={10}>
    <AnalyticsCardTitle title="Needs me" />
    <NeedsMeRow label="forms past due">
      <FormsPastDue myTasks={myTasks} />
    </NeedsMeRow>
    {self !== null && <SummaryRows self={self} />}
  </FlexCard>
);
